import { supabase } from "@/integrations/supabase/client";
import type { AppRole } from "@/hooks/useRoles";

export type ProfileRow = {
  id: string;
  email: string | null;
  display_name: string | null;
  created_at: string;
};

export type RoleRow = { user_id: string; role: AppRole };

export type AdvisorCodeRow = {
  code: string;
  advisor_user_id: string;
  active: boolean;
  discount_note: string | null;
  created_at: string;
};

export type AdvisorClientRow = {
  advisor_user_id: string;
  client_user_id: string;
  linked_via_code: string | null;
  created_at: string;
};

/* -------- Admin: profiles + roles -------- */
export async function listAllProfiles(): Promise<ProfileRow[]> {
  const { data, error } = await supabase
    .from("profiles")
    .select("id,email,display_name,created_at")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data as ProfileRow[];
}

export async function listAllRoles(): Promise<RoleRow[]> {
  const { data, error } = await supabase.from("user_roles").select("user_id, role");
  if (error) throw error;
  return data as RoleRow[];
}

export async function addUserRole(userId: string, role: AppRole) {
  const { error } = await supabase.from("user_roles").insert({ user_id: userId, role });
  if (error) throw error;
}

export async function removeUserRole(userId: string, role: AppRole) {
  const { error } = await supabase
    .from("user_roles")
    .delete()
    .eq("user_id", userId)
    .eq("role", role);
  if (error) throw error;
}

/* -------- Admin: advisor codes -------- */
export async function listAllAdvisorCodes(): Promise<AdvisorCodeRow[]> {
  const { data, error } = await supabase
    .from("advisor_codes")
    .select("code, advisor_user_id, active, discount_note, created_at")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data as AdvisorCodeRow[];
}

export async function createAdvisorCode(input: {
  code: string;
  advisor_user_id: string;
  discount_note?: string | null;
}) {
  const { error } = await supabase.from("advisor_codes").insert({
    code: input.code,
    advisor_user_id: input.advisor_user_id,
    discount_note: input.discount_note ?? null,
    active: true,
  });
  if (error) throw error;
}

export async function setAdvisorCodeActive(code: string, active: boolean) {
  const { error } = await supabase.from("advisor_codes").update({ active }).eq("code", code);
  if (error) throw error;
}

/* -------- Advisor: own code + linked clients -------- */
export async function listMyAdvisorCodes(userId: string): Promise<AdvisorCodeRow[]> {
  const { data, error } = await supabase
    .from("advisor_codes")
    .select("code, advisor_user_id, active, discount_note, created_at")
    .eq("advisor_user_id", userId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data as AdvisorCodeRow[];
}

export async function listMyLinkedClients(advisorId: string): Promise<
  Array<AdvisorClientRow & { profile: ProfileRow | null }>
> {
  const { data: links, error } = await supabase
    .from("advisor_clients")
    .select("advisor_user_id, client_user_id, linked_via_code, created_at")
    .eq("advisor_user_id", advisorId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  const ids = (links ?? []).map((l) => l.client_user_id);
  let profiles: ProfileRow[] = [];
  if (ids.length) {
    const { data: profs, error: pErr } = await supabase
      .from("profiles")
      .select("id,email,display_name,created_at")
      .in("id", ids);
    if (pErr) throw pErr;
    profiles = (profs ?? []) as ProfileRow[];
  }
  return (links ?? []).map((l) => ({
    ...(l as AdvisorClientRow),
    profile: profiles.find((p) => p.id === l.client_user_id) ?? null,
  }));
}

/** Read-only projection of a client scenario, as surfaced in the advisor table. */
export type AdvisorScenarioRow = {
  id: string;
  name: string | null;
  loan_id: string | null;
  is_draft: boolean;
  data: unknown;
  created_at: string;
  updated_at: string;
};

export async function listClientScenariosForAdvisor(
  clientId: string,
): Promise<AdvisorScenarioRow[]> {
  const { data, error } = await supabase
    .from("scenarios")
    .select("id, name, loan_id, is_draft, data, created_at, updated_at")
    .eq("user_id", clientId)
    .order("updated_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as AdvisorScenarioRow[];
}